import { createAsyncActionFlow } from '../../../components/async-action-flow.js';
import { createInlineFeedbackController } from '../../../components/inline-feedback.js';
import { readAccountPageCsrfToken } from '../dom.js';
import { submitAccountExport } from './api.js';
import { resolveAccountExportDom } from './dom.js';
import { formatExportAvailability } from './helpers.js';

export function initAccountExport(documentRef, fetchImpl, page, timerApi = globalThis) {
    const dom = resolveAccountExportDom(page);
    if (dom === null) {
        return;
    }

    const {
        availability,
        card,
        defaultTriggerLabel,
        exportUrl,
        feedback,
        form,
        initialReadyDownloadUrl,
        initialReadyExpiresAt,
        trigger,
    } = dom;
    const feedbackController = createInlineFeedbackController({ documentRef, feedback });
    let destroyed = false;
    const renderReadyFeedback = (createElement, downloadUrl, expiresAt, shouldAnimate = true) => {
        if (shouldAnimate) {
            feedback.dataset.revealPhase = feedback.dataset.revealPhase === 'a' ? 'b' : 'a';
        } else {
            delete feedback.dataset.revealPhase;
        }

        const icon = createElement('span');
        icon.className = 'account-export-feedback-icon';
        icon.textContent = '✓';
        icon.setAttribute('aria-hidden', 'true');

        const message = createElement('span');
        message.className = 'account-export-feedback-message';
        message.textContent = 'Export is ready';

        const availabilityLabel = formatExportAvailability(expiresAt);
        const detail = createElement('span');
        detail.className = 'account-export-feedback-detail';
        detail.textContent = availabilityLabel;
        detail.setAttribute('data-account-export-availability', '');

        const link = createElement('a');
        link.className = 'account-export-feedback-link';
        link.setAttribute('href', downloadUrl);
        link.textContent = 'Download export';

        feedback.append(icon, message);
        if (availabilityLabel !== '') {
            feedback.append(detail);
        }
        feedback.append(link);
    };

    const flow = createAsyncActionFlow({
        defaultTriggerLabel,
        feedbackController,
        root: card,
        stateConfig: {
            idle: {
                disabled: false,
                label: defaultTriggerLabel,
            },
            generating: {
                disabled: true,
                label: 'Generating...',
            },
            ready: {
                disabled: false,
                label: defaultTriggerLabel,
            },
            failed: {
                disabled: false,
                label: defaultTriggerLabel,
            },
            'rate-limited': {
                disabled: true,
                label: 'Try again later',
            },
        },
        stateDatasetKey: 'accountExportState',
        timerApi,
        trigger,
    });

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (destroyed || flow.isLocked()) {
            return;
        }

        flow.transition('generating', {
            message: 'Preparing export archive...',
            tone: 'warning',
        });

        try {
            const result = await submitAccountExport(fetchImpl, {
                csrfToken: readAccountPageCsrfToken(documentRef),
                exportUrl,
            });

            if (destroyed) {
                return;
            }

            if (result.kind === 'ready') {
                flow.transition('ready', {
                    renderContent: ({ createElement }) => renderReadyFeedback(
                        createElement,
                        result.downloadUrl,
                        result.expiresAt,
                        true,
                    ),
                    tone: 'success',
                });
                return;
            }

            if (result.kind === 'rate-limited') {
                flow.transition('rate-limited', {
                    cooldownSeconds: result.retryAfterSeconds,
                    message: result.message,
                    tone: 'warning',
                });
                return;
            }

            flow.transition('failed', {
                message: result.message,
                tone: result.tone,
            });
        } catch (error) {
            if (destroyed) {
                return;
            }

            console.error(error);
            flow.transition('failed', {
                message: 'Export is temporarily unavailable. Please try again later.',
                tone: 'error',
            });
        }
    };

    if (card.dataset.accountExportState === 'ready' && initialReadyDownloadUrl !== '') {
        if (availability !== null || initialReadyExpiresAt !== '') {
            feedbackController.showContent('success', ({ createElement }) => renderReadyFeedback(
                createElement,
                initialReadyDownloadUrl,
                initialReadyExpiresAt,
                false,
            ));
        }
    }

    form.addEventListener('submit', handleSubmit);

    return {
        destroy() {
            destroyed = true;
            form.removeEventListener('submit', handleSubmit);
            if (flow.isLocked()) {
                flow.transition('idle', { clearFeedback: true });
            }
            flow.destroy();
        },
        getState: flow.getState,
        isLocked: flow.isLocked,
        transition: flow.transition,
    };
}
